import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UploadCloud, PackageOpen, RefreshCw } from "lucide-react";

import { Card, CardBody, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import { Field, Input, Select, SwitchRow, Textarea } from "@/components/ui/Form.jsx";
import { TableWrap, Td, Th, Tr } from "@/components/ui/Table.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { EmptyState } from "@/components/ui/Display.jsx";
import { useConfirm, useToast } from "@/components/ui/feedback.js";
import { appVersionService } from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { formatDateTime } from "@/lib/format.js";
import { cx } from "@/lib/cx.js";

/*
 * Super Admin > App Versions.
 *
 * Publishes the desktop-agent builds the fleet self-updates from. Uploading a
 * build makes it the "latest" for that platform+arch — the agent polls
 * GET /api/app-version/latest and swaps itself in. Backend: AppVersion model +
 * /api/app-version. See desktop-python/tracker/DESKTOP_BLUEPRINT/DESKTOP_AUTO_UPDATE_PLAN.md (P2).
 */

const PLATFORMS = [
    { value: "mac", label: "macOS" },
    { value: "win", label: "Windows" },
];

// Valid arches per platform (the agent picks its own; these must match the
// backend enum + what the desktop client reports).
const ARCHES = {
    mac: [
        { value: "arm64", label: "Apple Silicon (arm64)" },
        { value: "x86_64", label: "Intel (x86_64)" },
    ],
    win: [
        { value: "x64", label: "x64" },
        { value: "arm64", label: "arm64" },
    ],
};

const platformLabel = (p) => PLATFORMS.find((x) => x.value === p)?.label ?? p;

function formatBytes(n) {
    if (!n) return "—";
    const units = ["B", "KB", "MB", "GB"];
    let v = n;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i += 1;
    }
    return `${v.toFixed(i ? 1 : 0)} ${units[i]}`;
}

export default function AppVersions() {
    const queryClient = useQueryClient();
    const toast = useToast();
    const confirm = useConfirm();

    const [platform, setPlatform] = useState("mac");
    const [arch, setArch] = useState("arm64");
    const [version, setVersion] = useState("");
    const [notes, setNotes] = useState("");
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [formError, setFormError] = useState("");

    const listQuery = useQuery({
        queryKey: ["sa-app-versions"],
        queryFn: () => appVersionService.list().then((r) => r.data?.data ?? []),
    });
    const invalidate = () => queryClient.invalidateQueries({ queryKey: ["sa-app-versions"] });

    // Fleet-wide auto-update master switch (defaults ON until the backend answers).
    const settingsQuery = useQuery({
        queryKey: ["sa-app-update-settings"],
        queryFn: () => appVersionService.getSettings().then((r) => r.data?.data ?? {}),
    });
    const autoUpdateEnabled = settingsQuery.data?.autoUpdateEnabled ?? true;

    const toggleAutoUpdate = useMutation({
        mutationFn: (enabled) => appVersionService.updateSettings({ autoUpdateEnabled: enabled }),
        onSuccess: (res) => {
            toast(
                res.data?.data?.autoUpdateEnabled
                    ? "Auto-update enabled — the fleet will update itself."
                    : "Auto-update paused — no device will self-update.",
                "success",
            );
            queryClient.invalidateQueries({ queryKey: ["sa-app-update-settings"] });
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not change the setting."), "error"),
    });

    const upload = useMutation({
        mutationFn: (formData) =>
            appVersionService.upload(formData, {
                onUploadProgress: (e) =>
                    setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
            }),
        onSuccess: (res) => {
            const v = res.data?.data;
            toast(`Published ${v?.version} for ${platformLabel(v?.platform)}/${v?.arch}`, "success");
            setVersion("");
            setNotes("");
            setFile(null);
            setProgress(0);
            setFormError("");
            invalidate();
        },
        onError: (error) => {
            setProgress(0);
            setFormError(apiErrorMessage(error, "Upload failed."));
        },
    });

    const setLatest = useMutation({
        mutationFn: (id) => appVersionService.setLatest(id),
        onSuccess: () => {
            toast("Marked as latest", "success");
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not set latest."), "error"),
    });

    const remove = useMutation({
        mutationFn: (id) => appVersionService.remove(id),
        onSuccess: () => {
            toast("Version deleted", "success");
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not delete."), "error"),
    });

    const onPlatform = (value) => {
        setPlatform(value);
        setArch(ARCHES[value][0].value); // keep arch valid for the new platform
    };

    const submit = (e) => {
        e.preventDefault();
        if (!version.trim()) return setFormError("Version is required (e.g. 2.1.1).");
        if (!file) return setFormError("Choose a build file.");
        const fd = new FormData();
        fd.append("platform", platform);
        fd.append("arch", arch);
        fd.append("version", version.trim());
        fd.append("notes", notes.trim());
        fd.append("file", file);
        setFormError("");
        upload.mutate(fd);
    };

    const askDelete = async (v) => {
        const ok = await confirm({
            tone: "danger",
            title: `Delete ${platformLabel(v.platform)}/${v.arch} ${v.version}?`,
            body: "This removes the catalog entry and its stored build file. Devices already on this version keep running; they just can no longer download it.",
            okLabel: "Delete build",
        });
        if (ok) remove.mutate(v.id);
    };

    /*
     * Array.isArray, not `?? []`. The endpoint answers with `data: [...]`, but
     * `??` only catches null and undefined — an error envelope, or a response
     * whose shape drifts to `{ versions: [...] }`, is an object, and `.map` on
     * it throws inside render and blanks the whole page rather than showing an
     * empty table.
     */
    const rows = Array.isArray(listQuery.data) ? listQuery.data : [];

    return (
        <div className="grid gap-5 p-4.5">
            {/* ---- Auto-update master switch (fleet-wide) ---- */}
            <Card>
                <CardHead>
                    <CardTitle icon={<RefreshCw className="size-4.5" />}>Auto-update</CardTitle>
                </CardHead>
                <CardBody>
                    <SwitchRow
                        label="Silent auto-update (master switch)"
                        hint="Master gate for the whole fleet. Each organization must ALSO opt in per platform (Settings › Auto-update) before its devices update — this switch alone does not push updates. Off pauses everyone regardless. Agents check ~90s after launch, then every 6h; manual installs always work."
                        checked={autoUpdateEnabled}
                        disabled={settingsQuery.isLoading || toggleAutoUpdate.isPending}
                        onChange={(e) => toggleAutoUpdate.mutate(e.target.checked)}
                    />
                </CardBody>
            </Card>

            {/* ---- Publish a build ---- */}
            <Card>
                <CardHead>
                    <CardTitle icon={<UploadCloud className="size-4.5" />}>Publish a build</CardTitle>
                </CardHead>
                <CardBody>
                    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <Field label="Platform">
                            <Select value={platform} onChange={(e) => onPlatform(e.target.value)}>
                                {PLATFORMS.map((p) => (
                                    <option key={p.value} value={p.value}>
                                        {p.label}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                        <Field label="Architecture">
                            <Select value={arch} onChange={(e) => setArch(e.target.value)}>
                                {ARCHES[platform].map((a) => (
                                    <option key={a.value} value={a.value}>
                                        {a.label}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                        <Field label="Version" hint="Semantic, no -dev (e.g. 2.1.1)">
                            <Input
                                value={version}
                                onChange={(e) => setVersion(e.target.value)}
                                placeholder="2.1.1"
                            />
                        </Field>
                        <Field label="Build file" hint="mac: WorkHost-<arch>.zip · win: WorkHost-x64.zip">
                            <input
                                type="file"
                                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                                className="block w-full cursor-pointer text-sm text-slate-600 file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-slate-200 dark:text-slate-300 dark:file:bg-slate-800 dark:hover:file:bg-slate-700"
                            />
                        </Field>
                        <Field label="Notes" optional className="sm:col-span-2 lg:col-span-4">
                            <Textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={2}
                                placeholder="Release notes (optional)"
                            />
                        </Field>
                        <div className="flex flex-wrap items-center gap-3 sm:col-span-2 lg:col-span-4">
                            <Button type="submit" variant="primary" disabled={upload.isPending}>
                                {upload.isPending
                                    ? `Uploading… ${progress}%`
                                    : "Upload + set as latest"}
                            </Button>
                            {file && !upload.isPending && (
                                <span className="text-sm text-slate-500">
                                    {file.name} · {formatBytes(file.size)}
                                </span>
                            )}
                            {formError && <span className="text-sm text-rose-600">{formError}</span>}
                        </div>
                    </form>
                </CardBody>
            </Card>

            {/* ---- Catalog ---- */}
            <Card>
                <CardHead>
                    <CardTitle icon={<PackageOpen className="size-4.5" />}>Published builds</CardTitle>
                </CardHead>
                {listQuery.isLoading ? (
                    <div className="px-4.5 py-10 text-center text-sm text-slate-400">Loading…</div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        title="No builds published yet"
                        hint="Upload a build above to start the fleet auto-updating."
                    />
                ) : (
                    <TableWrap minWidth={760}>
                        <thead>
                            <Tr>
                                <Th first>Platform</Th>
                                <Th>Arch</Th>
                                <Th>Version</Th>
                                <Th>Size</Th>
                                <Th>Status</Th>
                                <Th>Uploaded</Th>
                                <Th last>Actions</Th>
                            </Tr>
                        </thead>
                        <tbody>
                            {rows.map((v) => (
                                <Tr key={v.id} className={cx(v.isLatest && "bg-emerald-50/40 dark:bg-emerald-500/5")}>
                                    <Td first>{platformLabel(v.platform)}</Td>
                                    <Td>{v.arch}</Td>
                                    <Td className="font-medium">{v.version}</Td>
                                    <Td>{formatBytes(v.sizeBytes)}</Td>
                                    <Td>
                                        {v.isLatest ? (
                                            <Tag tone="emerald" dot>
                                                Latest
                                            </Tag>
                                        ) : (
                                            <span className="text-slate-400">—</span>
                                        )}
                                    </Td>
                                    <Td>{formatDateTime(v.createdAt)}</Td>
                                    <Td last>
                                        <div className="flex flex-wrap gap-2">
                                            {!v.isLatest && (
                                                <Button
                                                    onClick={() => setLatest.mutate(v.id)}
                                                    disabled={setLatest.isPending}
                                                >
                                                    Set latest
                                                </Button>
                                            )}
                                            <Button
                                                variant="dangerSoft"
                                                onClick={() => askDelete(v)}
                                                disabled={remove.isPending}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </Td>
                                </Tr>
                            ))}
                        </tbody>
                    </TableWrap>
                )}
            </Card>
        </div>
    );
}
